<template>
  <span
    class="coin-token"
    :class="[`is-${size}`, { shine, bob }]"
    :style="{ width: `${px}px`, height: `${px}px` }"
    aria-hidden="true"
  >
    <img
      :src="src"
      :srcset="srcset"
      :sizes="sizes"
      alt=""
      draggable="false"
    >
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type CoinTokenSize = 'sm' | 'md' | 'lg'

const PX: Record<CoinTokenSize, number> = {
  sm: 18,
  md: 28,
  lg: 56,
}

const SRC_256 = '/assets/ui/coin-token.webp'
const SRC_64 = '/assets/ui/coin-token@64.webp'
const SRC_24 = '/assets/ui/coin-token@24.webp'

const props = withDefaults(
  defineProps<{
    size?: CoinTokenSize
    shine?: boolean
    bob?: boolean
  }>(),
  { size: 'md', shine: false, bob: false },
)

const px = computed(() => PX[props.size])
const src = computed(() => {
  if (props.size === 'sm') return SRC_24
  if (props.size === 'md') return SRC_64
  return SRC_256
})
const srcset = `${SRC_24} 24w, ${SRC_64} 64w, ${SRC_256} 256w`
const sizes = computed(() => `${px.value}px`)
</script>

<style scoped>
.coin-token {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  border-radius: 50%;
  overflow: hidden;
  line-height: 0;
  vertical-align: middle;
  background: transparent;
}

.coin-token img {
  display: block;
  width: 106%;
  height: 106%;
  margin: -3%;
  object-fit: cover;
  pointer-events: none;
}

.coin-token.shine::after {
  content: '';
  position: absolute;
  inset: -40%;
  background: linear-gradient(
    115deg,
    transparent 40%,
    rgba(255, 255, 255, 0.45) 50%,
    transparent 60%
  );
  animation: coin-shine var(--md-motion-long, 500ms)
    var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1)) 2;
}

.coin-token.bob {
  animation: coin-bob 1.1s var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1))
    infinite;
}

@keyframes coin-shine {
  from { transform: translateX(-60%); }
  to { transform: translateX(60%); }
}

@keyframes coin-bob {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  40% { transform: translateY(-4px) rotate(-10deg); }
  70% { transform: translateY(-2px) rotate(8deg); }
}

@media (prefers-reduced-motion: reduce) {
  .coin-token.shine::after,
  .coin-token.bob {
    animation: none;
  }
}
</style>
